import { useEffect, useRef } from 'react';
import moment, { Moment } from 'moment';
import { List } from 'immutable';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { electronActions, electronListeners } from 'flow/utils/electron-utils';
import {
    selectExtensionProfiles,
    selectExtensionActiveProfileKey,
    selectExtensionActiveProfile,
    selectOwnExtensionKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import {
    getFocusedSession,
    getNumLocalCalls,
    getAgentInfo,
    isLocalFocusedCallSessionEstablished,
} from 'flow/selectors/call-selectors';
import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import { answerCall, hangupSession } from 'flow/actions/phone-actions';
import { activateExtensionProfile } from 'flow/actions/extension-actions';
import { getCallItemContactTitle } from 'flow/utils/contact-utils';
import { useModal } from 'flow/contexts/ModalProvider';
import {
    getStandardProfileDescriptionInUserLanguage,
    resolveDefaultDuration,
} from 'flow/utils/profile-utils';
import { getActiveUntil, ISO_8601_MS_RESOLUTION } from 'flow/utils';
import { store } from 'flow/configure-store';
import { isDesignatedCompanion } from 'flow/companion/companion-utils';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';

/**
 * Renderless component that bridges Redux state to the companion tray
 * (Electron main process) via IPC. Follows the same pattern as
 * DocumentTitleUserStatusHandler.
 *
 * Pushes profiles, call state and whether this install is acting as the
 * companion; listens for answer-call, hangup-call, activate-profile,
 * open-profile-modal.
 */
const CompanionTraySync = (): null => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const extensionKey = useAppSelector(selectOwnExtensionKey);
    const profiles = useAppSelector((state) =>
        selectExtensionProfiles(state, extensionKey),
    );
    const activeProfileKey = useAppSelector((state) =>
        selectExtensionActiveProfileKey(state, extensionKey),
    );
    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extensionKey),
    );
    const focusedSession = useAppSelector(getFocusedSession);
    const numCalls = useAppSelector(getNumLocalCalls);
    // Redux-level boolean that flips when the focused call is answered.
    // We track this separately from `focusedSession` because the JsSIP
    // session object is mutated in place (its `isEstablished()` flips
    // without the reference changing) — without a primitive dependency,
    // the useEffect below wouldn't re-fire on answer and the tray would
    // stay stuck in the ringing state.
    const focusedCallEstablished = useAppSelector(
        isLocalFocusedCallSessionEstablished,
    );
    const ownCountryCode = useAppSelector(selectUserCountryCode);
    // Drives whether the tray exists at all on macOS, so main has to be told
    // when it changes rather than only at startup.
    const isCompanion = useAppSelector(isDesignatedCompanion);

    // Listener callbacks are registered once on mount, but need access to
    // the latest extensionKey + profiles list each time they fire. Refs
    // give us a stable pointer whose contents follow the current render.
    const extensionKeyRef = useRef(extensionKey);
    extensionKeyRef.current = extensionKey;
    const profilesRef = useRef<List<TypedMap<Profile>> | null>(profiles);
    profilesRef.current = profiles;
    const openModalRef = useRef(openModal);
    openModalRef.current = openModal;

    // Push call state to main process when focused session or call count
    // changes. Idle when no local calls; otherwise derive kind from the
    // session's direction + isEstablished, and resolve the display text
    // through the same pipeline the in-call UI uses (contact name →
    // company → formatted number → "Unknown contact").
    useEffect(() => {
        if (numCalls === 0 || !focusedSession) {
            electronActions.sendCompanionTrayCallState({
                kind: 'idle',
                displayText: null,
                direction: null,
            });
            return;
        }

        const session = focusedSession as any;
        const remoteNumber = session.remote_identity?.uri?.user || null;
        const direction = session.direction || null;
        // Prefer the Redux-level flag (also in deps) so this stays in sync
        // with the dependency that triggers the effect; fall back to the
        // JsSIP session method if the selector hasn't populated yet.
        const isEstablished =
            focusedCallEstablished ||
            (typeof session.isEstablished === 'function'
                ? session.isEstablished()
                : false);

        // Resolve contact + agent info via the store so we get the same
        // display text as LargeCallItem / SmallCallItem show.
        const state = store.getState();
        const getContact = makeGetContactDataForNumber();
        // @ts-ignore — selector typings accept the number as a second arg
        const contact = getContact(state, remoteNumber);
        const callId = session.id || session._id;
        const agentInfo = callId ? getAgentInfo(state, callId) : undefined;

        const title = getCallItemContactTitle(
            contact,
            agentInfo,
            remoteNumber,
            ownCountryCode,
            i18next,
        );

        electronActions.sendCompanionTrayCallState({
            kind:
                !isEstablished && direction === 'incoming'
                    ? 'ringing'
                    : 'inCall',
            displayText: title?.text ?? null,
            direction,
        });
    }, [
        focusedSession,
        focusedCallEstablished,
        numCalls,
        ownCountryCode,
        i18next,
    ]);

    // Push profiles to main process when they change. `defaultDuration` and
    // `plusId` are included so the tray can (a) assign keyboard shortcuts
    // ordered by the same profile list and (b) hide the "Customize…"
    // submenu option on profiles that can't carry a custom end time
    // (plusId === 0, typically "Available").
    useEffect(() => {
        if (!profiles || !extensionKey) return;

        const serialized = profiles.toJS().map((p: any) => ({
            key: p.key,
            description: p.description,
            available: p.available,
            defaultDuration: p.defaultDuration,
            plusId: p.plusId,
        }));

        // Render the active profile's full label in the renderer (has access
        // to the same localization helpers as the in-app ProfilePicker). The
        // tray just displays the string — no duplicate formatting in main.
        // Matches the app header's "Designmöte until Tuesday 09:30" style.
        const activeProfileLabel = activeProfile
            ? [
                  getStandardProfileDescriptionInUserLanguage(
                      activeProfile.get('description'),
                      i18next,
                  ),
                  getActiveUntil(activeProfile, i18next),
              ]
                  .filter(Boolean)
                  .join(' ')
            : null;

        electronActions.sendCompanionTrayProfiles({
            profiles: serialized,
            activeProfileKey: activeProfileKey || null,
            activeProfileLabel,
            extensionKey,
        });
    }, [profiles, activeProfileKey, activeProfile, extensionKey, i18next]);

    // Tell main whether this install is acting as the companion. Sent on its
    // own rather than folded into the profiles payload above: it changes when
    // the user touches the setting or the backend switch resolves, neither of
    // which moves a profile.
    useEffect(() => {
        electronActions.sendCompanionTrayDesignation({ isCompanion });
    }, [isCompanion]);

    // Register IPC listeners for tray commands (once on mount).
    useEffect(() => {
        const findProfile = (profileKey: string): TypedMap<Profile> | null => {
            const list = profilesRef.current;
            if (!list) return null;
            return list.find((p) => p?.get('key') === profileKey) ?? null;
        };

        const activate = (profile: TypedMap<Profile>, until: Moment | null) => {
            const key = extensionKeyRef.current;
            if (!key) return;
            const updatedProfileDTO = profile
                .update('active', () => true)
                .update('activeUntil', () =>
                    until ? until.format(ISO_8601_MS_RESOLUTION) : null,
                );
            dispatch(activateExtensionProfile(key, updatedProfileDTO));
        };

        electronListeners.registerCompanionTrayAnswerListener(() => {
            const state = store.getState();
            const session = getFocusedSession(state);
            if (
                session &&
                session.direction === 'incoming' &&
                !session.isEstablished()
            ) {
                dispatch(answerCall(session));
            }
        });

        electronListeners.registerCompanionTrayHangupListener(() => {
            const state = store.getState();
            const session = getFocusedSession(state);
            if (session) {
                dispatch(hangupSession(session));
            }
        });

        electronListeners.registerCompanionTrayActivateProfileListener(
            (payload: { profileKey: string; mode: string }) => {
                const profile = findProfile(payload?.profileKey);
                if (!profile) return;
                const until =
                    payload.mode === 'default'
                        ? resolveDefaultDuration(
                              profile.get('defaultDuration'),
                              moment(),
                          )
                        : null;
                activate(profile, until);
            },
        );

        electronListeners.registerCompanionTrayOpenProfileModalListener(
            (payload: { profileKey: string }) => {
                const profile = findProfile(payload?.profileKey);
                if (!profile) return;
                openModalRef.current('setProfileUntil', {
                    profile,
                    onSubmit: (
                        submittedProfile: TypedMap<Profile>,
                        until: Moment,
                    ) => activate(submittedProfile, until),
                });
            },
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

export default CompanionTraySync;
